import React, { useState, useMemo } from 'react';
import { DefaultButton, Panel, PanelType, SearchBox, Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBoxesPacking, 
    faDolly, 
    faExternalLink, 
    faListCheck, 
    faRoute, 
    faShip, 
    faTruck, 
    faUser, 
    faXmark,
    faExclamationTriangle,
    faClock
} from '@fortawesome/pro-light-svg-icons';
import { NavLink } from 'react-router-dom';
import moment from 'moment';
import { IActiveOrdersRecap, IActiveOrderInfo, TOperationActivityType } from '../types';
import Text from '../../../typography/text';
import OrderService from '../../../../services/orders/order';

interface IActiveOrdersModalProps {
    isOpen: boolean;
    onDismiss(): void;
    recap: IActiveOrdersRecap;
    selectedDate: {
        startDate: string;
        endDate: string;
    };
}

const ActiveOrdersModal: React.FC<IActiveOrdersModalProps> = ({
    isOpen,
    onDismiss,
    recap,
    selectedDate,
}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [activityFilter, setActivityFilter] = useState<'all' | 'loading' | 'sailing' | 'unloading' | 'dooring'>('all');

    const formattedStart = moment(selectedDate.startDate).format('DD MMM YYYY');
    const formattedEnd = moment(selectedDate.endDate).format('DD MMM YYYY');

    const filteredOrders = useMemo(() => {
        return recap.activeOrders.filter((ord) => {
            // Activity filter
            if (activityFilter !== 'all') {
                const hasAct = ord.activities.some((act) => {
                    if (activityFilter === 'loading') return act.activityType === 'loading';
                    if (activityFilter === 'sailing') return act.activityType === 'ship_departure' || act.activityType === 'ship_arrival';
                    if (activityFilter === 'unloading') return act.activityType === 'unloading';
                    if (activityFilter === 'dooring') return act.activityType === 'dooring';
                    return true;
                });
                if (!hasAct) return false;
            }

            // Search query
            if (searchQuery.trim() !== '') {
                const q = searchQuery.toLowerCase();
                const matchOrder = ord.orderNumber.toLowerCase().includes(q) || OrderService.getOrderNumber(ord.orderNumber).toLowerCase().includes(q);
                const matchCust = ord.customerName?.toLowerCase().includes(q);
                const matchSales = ord.salesName?.toLowerCase().includes(q);
                if (!matchOrder && !matchCust && !matchSales) {
                    return false;
                }
            }

            return true;
        });
    }, [recap.activeOrders, activityFilter, searchQuery]);

    const getActivityIcon = (type: TOperationActivityType) => {
        switch (type) {
            case 'loading': return faBoxesPacking;
            case 'ship_departure':
            case 'ship_arrival': return faShip;
            case 'unloading': return faDolly;
            case 'dooring': return faTruck;
            default: return faRoute;
        }
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            isLightDismiss
            headerText=""
            styles={{
                content: { padding: '0 24px 24px 24px' },
                commands: { paddingTop: 12 },
            }}
        >
            {/* Header */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            backgroundColor: '#eff6ff',
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                        }}
                    >
                        <FontAwesomeIcon icon={faRoute} />
                    </div>
                    <div>
                        <Text size="large" style={{ fontWeight: 600, color: '#0f172a' }}>
                            Running Orders ({recap.totalActiveOrders})
                        </Text>
                        <Text size="xsmall" style={{ color: '#64748b' }}>
                            Period: {formattedStart} - {formattedEnd}
                        </Text>
                    </div>
                </div>
            </div>

            {/* Quick Metrics Bar (Minimalist & Aesthetic) */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 10,
                    marginBottom: 16,
                }}
            >
                {/* Loading */}
                <div
                    onClick={() => setActivityFilter(activityFilter === 'loading' ? 'all' : 'loading')}
                    style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: activityFilter === 'loading' ? '#f0f9ff' : '#ffffff',
                        border: `1px solid ${activityFilter === 'loading' ? '#0284c7' : '#e2e8f0'}`,
                        boxShadow: activityFilter === 'loading' ? '0 0 0 1px #0284c7' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>Loading</span>
                        <FontAwesomeIcon icon={faBoxesPacking} style={{ color: '#f59e0b', fontSize: 13 }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                        {recap.breakdown.loading}
                    </div>
                </div>

                {/* Sailing */}
                <div
                    onClick={() => setActivityFilter(activityFilter === 'sailing' ? 'all' : 'sailing')}
                    style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: activityFilter === 'sailing' ? '#f0f9ff' : '#ffffff',
                        border: `1px solid ${activityFilter === 'sailing' ? '#0284c7' : '#e2e8f0'}`,
                        boxShadow: activityFilter === 'sailing' ? '0 0 0 1px #0284c7' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>Sailing</span>
                        <FontAwesomeIcon icon={faShip} style={{ color: '#0d9488', fontSize: 13 }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                        {recap.breakdown.sailing}
                    </div>
                </div>

                {/* Unloading */}
                <div
                    onClick={() => setActivityFilter(activityFilter === 'unloading' ? 'all' : 'unloading')}
                    style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: activityFilter === 'unloading' ? '#f0f9ff' : '#ffffff',
                        border: `1px solid ${activityFilter === 'unloading' ? '#0284c7' : '#e2e8f0'}`,
                        boxShadow: activityFilter === 'unloading' ? '0 0 0 1px #0284c7' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>Unloading</span>
                        <FontAwesomeIcon icon={faDolly} style={{ color: '#8b5cf6', fontSize: 13 }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                        {recap.breakdown.unloading}
                    </div>
                </div>

                {/* Dooring */}
                <div
                    onClick={() => setActivityFilter(activityFilter === 'dooring' ? 'all' : 'dooring')}
                    style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: activityFilter === 'dooring' ? '#f0f9ff' : '#ffffff',
                        border: `1px solid ${activityFilter === 'dooring' ? '#0284c7' : '#e2e8f0'}`,
                        boxShadow: activityFilter === 'dooring' ? '0 0 0 1px #0284c7' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>Dooring</span>
                        <FontAwesomeIcon icon={faTruck} style={{ color: '#10b981', fontSize: 13 }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                        {recap.breakdown.dooring}
                    </div>
                </div>
            </div>

            {/* Filter Tabs & Search */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                <SearchBox
                    placeholder="Search by order number, customer, sales..."
                    value={searchQuery}
                    onChange={(_, val) => setSearchQuery(val || '')}
                    onClear={() => setSearchQuery('')}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={() => setActivityFilter('all')}
                        style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: activityFilter === 'all' ? '#0284c7' : '#e2e8f0',
                            background: activityFilter === 'all' ? '#0284c7' : '#ffffff',
                            color: activityFilter === 'all' ? '#ffffff' : '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        All Active ({recap.totalActiveOrders})
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivityFilter('loading')}
                        style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: activityFilter === 'loading' ? '#0284c7' : '#e2e8f0',
                            background: activityFilter === 'loading' ? '#0284c7' : '#ffffff',
                            color: activityFilter === 'loading' ? '#ffffff' : '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        Loading ({recap.breakdown.loading})
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivityFilter('sailing')}
                        style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: activityFilter === 'sailing' ? '#0284c7' : '#e2e8f0',
                            background: activityFilter === 'sailing' ? '#0284c7' : '#ffffff',
                            color: activityFilter === 'sailing' ? '#ffffff' : '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        Sailing ({recap.breakdown.sailing})
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivityFilter('unloading')}
                        style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: activityFilter === 'unloading' ? '#0284c7' : '#e2e8f0',
                            background: activityFilter === 'unloading' ? '#0284c7' : '#ffffff',
                            color: activityFilter === 'unloading' ? '#ffffff' : '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        Unloading ({recap.breakdown.unloading})
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivityFilter('dooring')}
                        style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: activityFilter === 'dooring' ? '#0284c7' : '#e2e8f0',
                            background: activityFilter === 'dooring' ? '#0284c7' : '#ffffff',
                            color: activityFilter === 'dooring' ? '#ffffff' : '#475569',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        Dooring ({recap.breakdown.dooring})
                    </button>
                </div>
            </div>

            {/* Orders List Table */}
            <div
                style={{
                    maxHeight: 'calc(100vh - 360px)',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                }}
            >
                {filteredOrders.length === 0 ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                        <Text size="medium">No active orders matching this filter.</Text>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: '#475569' }}>
                                    Order & Customer
                                </th>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: '#475569' }}>
                                    Sales Person
                                </th>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: '#475569' }}>
                                    Active Activities
                                </th>
                                <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, color: '#475569', width: 90 }}>
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((ord, idx) => (
                                <tr
                                    key={ord.orderId || idx}
                                    style={{
                                        borderBottom: '1px solid #f1f5f9',
                                        background: ord.hasOverdue ? '#fffafa' : '#ffffff',
                                    }}
                                >
                                    {/* Order & Customer */}
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        <Stack tokens={{ childrenGap: 2 }}>
                                            <NavLink
                                                to={`/orders/${ord.orderId}`}
                                                style={{ textDecoration: 'none', color: '#0284c7', fontWeight: 600 }}
                                            >
                                                #{OrderService.getOrderNumber(ord.orderNumber)}
                                            </NavLink>
                                            {ord.customerName && (
                                                <Text size="xsmall" style={{ color: '#64748b' }}>
                                                    {ord.customerName}
                                                </Text>
                                            )}
                                        </Stack>
                                    </td>

                                    {/* Sales Person */}
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        {ord.salesName ? (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 22,
                                                        height: 22,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#eff6ff',
                                                        color: '#2563eb',
                                                        border: '1px solid #bfdbfe',
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {ord.salesName.charAt(0).toUpperCase()}
                                                </span>
                                                <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                                    {ord.salesName}
                                                </Text>
                                            </div>
                                        ) : (
                                            <span style={{ color: '#94a3b8' }}>-</span>
                                        )}
                                    </td>

                                    {/* Activities */}
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {ord.activities.map((act) => (
                                                <div
                                                    key={act.id}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        fontSize: 11,
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: 6,
                                                        padding: '3px 8px',
                                                        width: 'fit-content',
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={getActivityIcon(act.activityType)}
                                                        style={{ color: act.activityColor, fontSize: 11 }}
                                                    />
                                                    <span style={{ fontWeight: 600, color: '#334155' }}>
                                                        {act.activityLabel}
                                                    </span>

                                                    {act.isOverdue ? (
                                                        <span
                                                            style={{
                                                                color: '#dc2626',
                                                                fontWeight: 600,
                                                                background: '#fee2e2',
                                                                borderRadius: 4,
                                                                padding: '1px 5px',
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            Overdue {act.overdueDays ? `${act.overdueDays}d` : ''}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color: act.normalizedStatus === 'in_progress' ? '#0284c7' : '#64748b',
                                                                fontWeight: 500,
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            {act.statusLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </td>

                                    {/* Action */}
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top', textAlign: 'center' }}>
                                        <NavLink
                                            to={`/orders/${ord.orderId}`}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: '#0284c7',
                                                textDecoration: 'none',
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                background: '#f0f9ff',
                                                border: '1px solid #bae6fd',
                                            }}
                                        >
                                            <span>Open</span>
                                            <FontAwesomeIcon icon={faExternalLink} style={{ fontSize: 9 }} />
                                        </NavLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Footer */}
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                <DefaultButton text="Close" onClick={onDismiss} />
            </div>
        </Panel>
    );
};

export default ActiveOrdersModal;

